import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LinkComponent } from '@components/link/link.component';
import { Router } from '@angular/router';
import { ApiService, UserInfo } from '../../../services/api.service';
import { HomeLinksService } from '../../../services/home-links.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuService } from 'app/services/menu.service';
import { MenuItemType } from 'app/interfaces/api/MenuItemType';

@Component({
    selector: 'app-kblevel-2026',
    standalone: true,
    imports: [CommonModule, LinkComponent, TranslateModule],
    templateUrl: './kblevel-2026.component.html',
    styleUrl: './kblevel-2026.component.scss'
})
export class Kblevel2026Component implements OnInit {
    userInfo?: UserInfo
    availableActios: number = 0
    currentSales: number = 0
    nextLevelSales: number = 0
    currentExpositions: number = 0
    nextLevelExpositions: number = 0
    
    completedDisplays: number = 0
    progressText: string = ''
    public canExchangePoints: boolean = false

    constructor(
        private api: ApiService,
        private translate: TranslateService,
        private homeLinksService: HomeLinksService,
        private menuService: MenuService
    ) {}

    private appendDisplaysText(message: string, displaysNeeded: number): string {
        if (displaysNeeded <= 0) return message
        
        const word = displaysNeeded === 1 
            ? this.translate.instant('benefits_kb.loyalty_2026.exposition_singular')
            : this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
        
        const suffix = this.translate.instant('benefits_kb.loyalty_2026.and_x_expositions', { 
            count: displaysNeeded, 
            word: word 
        })
        
        return message + suffix
    }

    ngOnInit(): void {
        this.api.userInfo.subscribe((userInfo) => {
            this.userInfo = userInfo
            setTimeout(() => this.updateProgressText(), 0)
            
            if (userInfo.loyaltyLevel && userInfo.country) {
                this.api.getNextLoyaltyLevelRequirements(userInfo.loyaltyLevel, userInfo.country).subscribe((requirements) => {
                    this.nextLevelSales = requirements.ytdSales || 0
                })
            }
            // set permission from API-provided userInfo if available
            this.canExchangePoints = Array.isArray((userInfo as any).authorities) && (userInfo as any).authorities.some((a: any) => a === 'canExchangePoints')
        })

        // Recuperar datos desde localStorage y simplificar exposiciones a partir de completedDisplays
        const userInfo = localStorage.getItem('weUserInfo')
        if (userInfo) {
            const { availableActios, ytdSales, completedDisplays, Completed_Displays__c, authorities } = JSON.parse(userInfo)
            this.availableActios = availableActios || 0
            this.currentSales = ytdSales || 0
            // Priorizar el valor de completedDisplays (o Completed_Displays__c) y usarlo directamente como currentExpositions
            this.completedDisplays = (completedDisplays ?? Completed_Displays__c ?? 0) || 0
            this.currentExpositions = this.completedDisplays
            this.canExchangePoints = Array.isArray(authorities) && authorities.some((authority: any) => authority === 'canExchangePoints')
            setTimeout(() => this.updateProgressText(), 0)
        }
        
        // Actualizar cuando cambie el idioma
        this.translate.onLangChange.subscribe(() => {
            this.updateProgressText()
        })
    }
    
    updateProgressText(): void {
        this.progressText = this.getProgressText()
    }

    getLoyaltyProgress(): number {
        // Progreso ponderado: ventas y displays tienen el mismo peso (50% cada uno)
        // Elite: 12 ventas + 2 displays | Premium: 24 ventas + 3 displays
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0

        const clamp = (v: number, max: number) => Math.max(0, Math.min(v, max))

        // Determinar si alcanzó Elite para calcular progreso hacia Premium
        const reachedElite = sales >= 12 && displays >= 2
        
        // Objetivos de Elite
        const eliteSales = 12
        const eliteDisplays = 2
        
        // Objetivos de Premium
        const premiumSales = 24
        const premiumDisplays = 3

        if (!reachedElite) {
            // Progreso hacia Elite (0-50% de la barra total)
            const salesProgress = (clamp(sales, eliteSales) / eliteSales) * 50 // 50% del peso
            const displaysProgress = (clamp(displays, eliteDisplays) / eliteDisplays) * 50 // 50% del peso
            const eliteProgress = (salesProgress + displaysProgress) / 100 // Normalizar a 0-1
            return eliteProgress * 50 // Elite representa el 50% de la barra total (0-50%)
        } else {
            // Progreso hacia Premium (50-100% de la barra total)
            const salesProgress = (clamp(sales, premiumSales) / premiumSales) * 50
            const displaysProgress = (clamp(displays, premiumDisplays) / premiumDisplays) * 50
            const premiumProgress = (salesProgress + displaysProgress) / 100
            return 50 + (premiumProgress * 50) // 50% base de Elite + progreso a Premium (0-50%)
        }
    }
    
    getNextYearLevel(): string {
        // Determinar qué nivel tendrá el próximo año según sus logros actuales
        if (this.currentSales >= 24 && this.currentExpositions >= 3) {
            return 'premium'
        } else if (this.currentSales >= 12 && this.currentExpositions >= 2) {
            return 'elite'
        } else {
            return 'advanced'
        }
    }
    
    getProgressText(): string {
        if (!this.userInfo?.loyaltyLevel) return ''
        
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        const eliteSales = 12, eliteDisplays = 2
        const premiumSales = 24, premiumDisplays = 3

        // Usuario Premium
        if (currentLevel === 'premium') {
            const hasReachedPremiumGoals = sales >= premiumSales && displays >= premiumDisplays
            
            if (hasReachedPremiumGoals) {
                // PREMIUM CON OBJETIVOS CUMPLIDOS
                return this.translate.instant('benefits_kb.loyalty_2026.keep_registering_update_displays')
            } else {
                // PREMIUM SIN OBJETIVOS CUMPLIDOS
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.maintain_level_premium_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            }
        }

        // Usuario Elite
        if (currentLevel === 'elite') {
            const hasReachedEliteGoals = sales >= eliteSales && displays >= eliteDisplays
            
            if (hasReachedEliteGoals) {
                // ELITE CON OBJETIVOS CUMPLIDOS - nivel up hacia Premium
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.level_up_premium_requirements_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            } else {
                // ELITE SIN OBJETIVOS CUMPLIDOS - mantener nivel
                const salesNeeded = Math.max(eliteSales - sales, 0)
                const displaysNeeded = Math.max(eliteDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.maintain_level_requirements_elite_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            }
        }

        // Usuario Advanced
        if (displays === 0) {
            // ADVANCED SIN PUNTOS (sin displays)
            return this.translate.instant('benefits_kb.loyalty_2026.level_up_requirements')
        } else {
            // ADVANCED (con al menos un display)
            const salesNeeded = Math.max(eliteSales - sales, 0)
            const displaysNeeded = Math.max(eliteDisplays - displays, 0)
            const salesWord = salesNeeded === 1 ? 
                this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
            const displaysWord = displaysNeeded === 1 ? 
                this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
            return this.translate.instant('benefits_kb.loyalty_2026.level_up_one_display_dynamic', { 
                sales: salesNeeded,
                salesWord: salesWord,
                displays: displaysNeeded,
                displaysWord: displaysWord
            })
        }
    }
    
    getSalesObjective(): number {
        if (!this.userInfo?.loyaltyLevel) return 12
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        
        // Si es Premium, siempre mostrar objetivo Premium
        if (currentLevel === 'premium') return 24
        
        // Si es Elite
        if (currentLevel === 'elite') {
            // Si ya alcanzó los objetivos de Elite, mostrar objetivo Premium
            const hasReachedEliteGoals = sales >= 12 && displays >= 2
            return hasReachedEliteGoals ? 24 : 12
        }
        
        // Advanced u otros: mostrar objetivo Elite
        return 12
    }
    
    getDisplaysObjective(): number {
        if (!this.userInfo?.loyaltyLevel) return 2
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        
        // Si es Premium, siempre mostrar objetivo Premium
        if (currentLevel === 'premium') return 3
        
        // Si es Elite
        if (currentLevel === 'elite') {
            // Si ya alcanzó los objetivos de Elite, mostrar objetivo Premium
            const hasReachedEliteGoals = sales >= 12 && displays >= 2
            return hasReachedEliteGoals ? 3 : 2
        }
        
        // Advanced u otros: mostrar objetivo Elite
        return 2
    }
    
    hasAchievedObjectives(): boolean {
        if (!this.userInfo?.loyaltyLevel) return false
        
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        
        // Solo para Premium, verificar si ha alcanzado todos los objetivos
        if (currentLevel === 'premium') {
            return this.currentSales >= 24 && this.currentExpositions >= 3
        }
        
        return false
    }
    
    getCurrentYearDeadline(): string {
        const currentYear = new Date().getFullYear()
        const deadlineDate = new Date(currentYear, 11, 31) // 31 de diciembre del año actual
        const currentLang = this.translate.currentLang || 'es'
        
        // Mapear idiomas a locales
        const localeMap: { [key: string]: string } = {
            'es': 'es-ES',
            'en': 'en-US',
            'fr': 'fr-FR',
            'de': 'de-DE',
            'it': 'it-IT',
            'pt': 'pt-BR'
        }
        
        const locale = localeMap[currentLang] || 'es-ES'
        
        return deadlineDate.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    gotoBenefits() {
        this.homeLinksService.goToBenefits()
    }

    registerSale() {
        // Navegar a registro de ventas
        this.homeLinksService.goToSales()
    }

    requestExposition() {
        // Navegar a solicitud de exposición
        this.homeLinksService.goToDisplays()
    }

    exchangePoints() {
        // Navegar a canje de puntos (obtener URL del proveedor de rewards)
        if (!this.canExchangePoints) return
                this.api.getRewardsProviderUrl().subscribe({
                    next: (next) => {
                        this.menuService.goTo({ type: MenuItemType.EXTERNAL_LINK, code: 'xoxoday', link: next.redirectUrl, requiresContract: 'retail-rewards' })
                        // window.open(next.redirectUrl, '_blank')
                    },
                })
    }

    discoverMore() {
        this.homeLinksService.goToBenefits()
    }
}